import { useState } from "react";
import api, { formatApiError, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { PackageCheck, Truck, Warehouse, ChevronDown, Check } from "lucide-react";

export const DELIVERY_STEPS = [
  { value: "ordered", label: "Commandée", icon: PackageCheck, tone: "bg-slate-100 text-slate-600" },
  { value: "at_wholesaler", label: "Chez le grossiste", icon: Warehouse, tone: "bg-amber-100 text-amber-700" },
  { value: "delivered", label: "Livrée", icon: Truck, tone: "bg-emerald-100 text-emerald-700" },
];

export function DeliveryBadge({ status }) {
  const step = DELIVERY_STEPS.find((s) => s.value === status) || DELIVERY_STEPS[0];
  const Icon = step.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${step.tone}`} data-testid={`delivery-badge-${status || "ordered"}`}>
      <Icon className="h-3.5 w-3.5" /> {step.label}
    </span>
  );
}

export function DeliveryTimeline({ history }) {
  if (!history || !history.length) return null;
  return (
    <div className="mt-2 space-y-1">
      {history.map((h, i) => {
        const step = DELIVERY_STEPS.find((s) => s.value === h.status) || DELIVERY_STEPS[0];
        return (
          <div key={i} className="text-xs text-slate-500 flex items-center gap-2" data-testid={`timeline-${i}`}>
            <Check className="h-3 w-3 text-emerald-600" />
            <span className="font-semibold text-slate-700">{step.label}</span>
            <span className="text-slate-400">— {fmtDate(h.at)} · {h.by_name || h.by_role}</span>
          </div>
        );
      })}
    </div>
  );
}

export function DeliveryUpdater({ participationId, current, onUpdated, testId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = async (status) => {
    if (status === current) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.patch(`/participations/${participationId}/delivery`, { delivery_status: status });
      toast.success("Statut de livraison mis à jour");
      setOpen(false);
      onUpdated && onUpdated(status);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        disabled={busy}
        data-testid={testId || `set-delivery-${participationId}`}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors duration-200"
      >
        Modifier <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[180px] overflow-hidden" data-testid={`delivery-menu-${participationId}`}>
          {DELIVERY_STEPS.map((s) => (
            <button
              key={s.value}
              onClick={() => set(s.value)}
              data-testid={`delivery-opt-${participationId}-${s.value}`}
              className={`w-full px-3 py-2 text-left text-xs font-semibold hover:bg-slate-50 flex items-center gap-2 ${current === s.value ? "text-indigo-600" : "text-slate-700"}`}
            >
              <s.icon className="h-3.5 w-3.5" /> {s.label}
              {current === s.value && <Check className="h-3 w-3 ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
